import { StatusCode } from "api/protocols";
import { GetLoyaltyProgramOffers, GetLoyaltyProgramRewards, GetLoyaltyProgramTiers,PutLoyaltyProgram } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import axios, { CancelToken } from "axios";
import Page from "components/page/Page";
import Tabs from "components/tabs/Tabs";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { isEqualWith, isNull } from "lodash";
import React, { SetStateAction, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { loadLoyaltyProgram } from "redux/actions/facility";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { ILoyaltyProgram, IOffer, IReward, ITier } from "redux/reducers/models/loyalty";
import { ICategories, IDepartments } from "redux/reducers/models/product";
import "./loyalty.scss";
import LoyaltyCustomers from "./loyaltyTabs/LoyaltyCustomers";
import LoyaltyOverview from "./loyaltyTabs/LoyaltyOverview";
import LoyaltySettings from "./loyaltyTabs/LoyaltySettings";
import LoyaltyTerms from "./loyaltyTabs/LoyaltyTerms";

enum LoyaltyTab {
    Overview = 0,
    Customers = 1,
    Settings = 2,
    Terms = 3,
}


export default function LoyaltyProgramEdit() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const {facilityStore} = useAppSelector(store => store);
    const [loyaltyProgram, setLoyaltyProgram] = useState<ILoyaltyProgram>({...facilityStore?.loyaltyProgram}); 
    const [loyaltyProgramBeforeState, setLoyaltyProgramBeforeState] = useState<ILoyaltyProgram>(loyaltyProgram);
    const [tab, setTab] = useState<LoyaltyTab>(LoyaltyTab.Overview);

    const [offers, setOffers] = useState<Array<IOffer>>([]);
    const [offersBeforeState, setOffersBeforeState] = useState<Array<IOffer>>(offers);

    const [rewards, setRewards] = useState<Array<IReward>>([]);

    const [tiers, setTiers] = useState<Array<ITier & {rewards?: Array<IReward>}>>([]);
    const [tiersBeforeState, setTiersBeforeState] = useState<Array<ITier & {rewards?: Array<IReward>}>>(tiers);

    const [departments, setDepartments] = useState<Array<IDepartments & {cateogries?: ICategories[]}>>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<Array<number>>([]);
    const [selectedDepartmentsBeforeState, setSelectedDepartmentsBeforeState] = useState<Array<number>>(selectedDepartments);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (facilityStore?.loyaltyProgram) {
            if (facilityStore?.loyaltyProgram?.type === "points_based") {
                void loadPointsBased(source.token);
            } else {
                void loadTiersBased();
            }
            void loadDepartments(source.token);
            if (facilityStore?.loyaltyProgram?.excluded_departments && facilityStore?.loyaltyProgram?.excluded_departments?.length > 0) {
                ReactDOM.unstable_batchedUpdates(() => {
                    setSelectedDepartments(facilityStore?.loyaltyProgram?.excluded_departments?.map(department => department?.id));
                    setSelectedDepartmentsBeforeState(facilityStore?.loyaltyProgram?.excluded_departments?.map(department => department?.id));
                });
            }
        }
        return () => source.cancel();
    }, []);

    async function loadDepartments(cancelToken?: CancelToken) {
        const res = await GetDepartments({extended: true}, false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            setDepartments([]);
            dispatch(showError(t("Error getting departments")));
            return;
        }
        setDepartments(res?.data?.filter(department => department?.type === "department"));
    }

    async function loadPointsBased(cancelToken?: CancelToken) {
        dispatch(enqueue());
        const [offerRes, rewardRes] = await Promise.all([GetLoyaltyProgramOffers(false, cancelToken), GetLoyaltyProgramRewards(false, cancelToken)]);
        dispatch(dequeue());
        if (offerRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting offers")));
            return;
        }

        if (rewardRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting rewards")));
            return;
        }

        ReactDOM.unstable_batchedUpdates(() => {
            setOffers(offerRes?.data as SetStateAction<IOffer[]>);
            setOffersBeforeState(offerRes?.data as SetStateAction<IOffer[]>);
            setRewards(rewardRes?.data as SetStateAction<IReward[]>);
        });
    }

    async function loadTiersBased(cancelToken?: CancelToken) {
        dispatch(enqueue());
        const [tierRes, rewardRes] = await Promise.all([GetLoyaltyProgramTiers(false, cancelToken), GetLoyaltyProgramRewards(false, cancelToken)]);
        dispatch(dequeue());
        if (tierRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting tiers")));
            return;
        }

        if (rewardRes?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting rewards")));
            return;
        }

        const tiers: Array<ITier> = tierRes?.data;
        const rewards: Array<IReward> = rewardRes?.data;
        const updatedTiers = tiers?.map(tier => ({...tier, rewards: rewards?.filter(reward => reward?.loyalty_program_tier_id === tier?.id)}));

        ReactDOM.unstable_batchedUpdates(() => {
            setTiers(updatedTiers);
            setTiersBeforeState(updatedTiers);
            setRewards(rewardRes?.data as SetStateAction<IReward[]>);
        });
    }

    const tabs = [
        {
          id: "general-1",
          content: t("Overview"),
        },
    
        {
          id: "customers-1",
          content: t("Customers"),
        },
        {
            id: "settings-1",
            content: t("Settings"),
          },
          {
            id: "terms-1",
            content: t("Terms & Conditions"),
          },
      ];

    async function handleUnsavedChanges() {
        const res = await PutLoyaltyProgram(
            {...loyaltyProgram, 
            offers: JSON.stringify(offers) !== JSON.stringify(offersBeforeState) ? offers : undefined ,
            tiers: JSON.stringify(tiers) !== JSON.stringify(tiersBeforeState) ? tiers : undefined,
            excluded_departments_ids: JSON.stringify(selectedDepartments) !== JSON.stringify(selectedDepartmentsBeforeState) ? selectedDepartments : undefined,
        }, true);
        if (res?.status !== StatusCode.OK) {
            dispatch(showError(t("Error updating offers")));
            return;
        }
        dispatch(showSuccess(t("Successfully updated loyalty program")));

        if (JSON.stringify(loyaltyProgram) !== JSON.stringify(loyaltyProgramBeforeState) || JSON.stringify(selectedDepartments) !== JSON.stringify(selectedDepartmentsBeforeState)) {
            const loyaltyUpdated = res?.data as ILoyaltyProgram;
            if (loyaltyUpdated) {
                ReactDOM.unstable_batchedUpdates(() => {
                    void dispatch(loadLoyaltyProgram(loyaltyUpdated));
                    setLoyaltyProgram(loyaltyUpdated);
                    setLoyaltyProgramBeforeState(loyaltyUpdated);
                    setSelectedDepartments(loyaltyUpdated?.excluded_departments?.map(department => department?.id));
                    setSelectedDepartmentsBeforeState(loyaltyUpdated?.excluded_departments?.map(department => department?.id));
                });
            } 
        }
 
        if (facilityStore?.loyaltyProgram?.type === "points_based") {
            await loadPointsBased();
        } else {
            await loadTiersBased();
        }
    }

    function unsavedChangesExist() {
        if (tiersBeforeState === null || offersBeforeState === null || loyaltyProgramBeforeState === null || selectedDepartments === null) {
          if (tiers) {
            setTiersBeforeState(tiers);
          }

          if (offers) {
              setOffersBeforeState(offers);
          }

          if (facilityStore?.loyaltyProgram) {
              setLoyaltyProgramBeforeState(facilityStore?.loyaltyProgram);
          }

          if (selectedDepartments) {
            setSelectedDepartmentsBeforeState(selectedDepartments);
        }
          return false;
        }
    
        return !isEqualWith(tiersBeforeState, tiers, (originalValue, newValue) => {
          if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
            return true;
          }
        }) || !isEqualWith(offersBeforeState, offers, (originalValue, newValue) => {
            if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
              return true;
            }
          }) || !isEqualWith(loyaltyProgramBeforeState, loyaltyProgram, (originalValue, newValue) => {
            if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
              return true;
            }
          }) || !isEqualWith(selectedDepartmentsBeforeState, selectedDepartments, (originalValue, newValue) => {
            if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
              return true;
            }
          });
      }

      function onUnsavedCancel() {
          ReactDOM.unstable_batchedUpdates(() => {
            setTiers(tiersBeforeState);
            setOffers(offersBeforeState);
            setLoyaltyProgram(loyaltyProgramBeforeState);
            setSelectedDepartments(selectedDepartmentsBeforeState);
          });
      }

    return (
        <Page
        narrow
        title={t("Loyalty Program")}
        subtitle={t("Edit details about your loyalty program")}
        notificationBarProps={{
            isVisible: unsavedChangesExist(),
            onAction: () => handleUnsavedChanges(),
            onCancel: () => onUnsavedCancel(),
          }}
        >
            <Tabs tabs={tabs} selected={tab} onSelect={tab => setTab(tab)}>
                <br />
                {tab === LoyaltyTab.Overview ? (
                <LoyaltyOverview
                    offers={offers}
                    rewards={rewards}
                    tiers={tiers}
                    setOffers={setOffers}
                    setTiers={setTiers}
                    loadPointsBased={loadPointsBased}
                    loadTiersBased={loadTiersBased}
                    />
                ) : tab === LoyaltyTab.Customers ? (
                    <LoyaltyCustomers/>
                ) : tab === LoyaltyTab.Settings ? (
                    <LoyaltySettings 
                        loyaltyProgram={loyaltyProgram} 
                        setLoyaltyProgram={setLoyaltyProgram} 
                        departments={departments} 
                        selectedDepartments={selectedDepartments}
                        setSelectedDepartments={setSelectedDepartments}
                    />
                ) : tab === LoyaltyTab.Terms ? (
                    <LoyaltyTerms
                    loyaltyProgram={loyaltyProgram} 
                    setLoyaltyProgram={setLoyaltyProgram} 
                    />
                ) : null}
            </Tabs>
        </Page>
    );
}